import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { acknowledgeEmailFailure, listEmailLog } from "@/lib/email-log.functions";
import { EMAIL_STATUS_LABELS, type EmailAckFilter } from "@/lib/email-log.types";
import { FAILED_SEND_STATUSES } from "@/lib/email-failures";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminCard } from "@/components/admin/AdminCard";
import { AdminSkeletonList } from "@/components/admin/AdminSkeletonList";
import { AdminErrorNotice } from "@/components/admin/AdminErrorNotice";
import { AdminEmptyState } from "@/components/admin/AdminEmptyState";
import { Button } from "@/components/ui/button";
import { buildEmailFailureFixPrompt } from "@/lib/fix-prompt.functions";
import { FixPromptButton } from "@/components/admin/FixPromptButton";

type Search = { status?: string; tage?: string; quittung?: string };

export const Route = createFileRoute("/_admin/admin/email-log")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    status: typeof search.status === "string" ? search.status : undefined,
    tage: typeof search.tage === "string" ? search.tage : undefined,
    quittung: typeof search.quittung === "string" ? search.quittung : undefined,
  }),
  component: EmailLogPage,
});

const dateFmt = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const ACK_FILTERS: Array<{ value: EmailAckFilter; label: string }> = [
  { value: "offen", label: "Offen" },
  { value: "quittiert", label: "Quittiert" },
  { value: "alle", label: "Alle" },
];

const FAILED = new Set<string>(FAILED_SEND_STATUSES);

function EmailLogPage() {
  const { status, tage, quittung } = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  // Ticket SK12: Dritte Sicht „Ohne Quittung" – Vormerkungen, die auf
  // „ausstehend" stehen geblieben sind.
  const onlyPending = status === "ausstehend";
  const onlyFailures = !onlyPending && status !== "alle";
  // Ticket W8: `0` = ohne Zeitgrenze. Endgültige Fehlschläge dürfen nicht
  // durch Zeitablauf aus dem Blick fallen.
  const days = tage === "0" ? 0 : Number(tage) > 0 ? Number(tage) : 7;
  const ack: EmailAckFilter =
    quittung === "quittiert" || quittung === "alle" || quittung === "offen"
      ? quittung
      : onlyFailures || onlyPending
        ? "offen"
        : "alle";

  const listFn = useServerFn(listEmailLog);
  const ackFn = useServerFn(acknowledgeEmailFailure);
  const fixPromptFn = useServerFn(buildEmailFailureFixPrompt);
  const q = useQuery({
    queryKey: ["admin", "email-log", onlyFailures, onlyPending, days, ack],
    queryFn: () => listFn({ data: { days, onlyFailures, onlyPending, ack } }),
    staleTime: 30_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "email-log"] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "dashboard"] });
  };

  const ackMutation = useMutation({
    mutationFn: (ids: string[]) => ackFn({ data: { ids } }),
    onSuccess: (result) => {
      toast.success(
        result.acknowledged === 1
          ? "Nachricht quittiert. Der Vorgang steht im Änderungsprotokoll."
          : `${result.acknowledged} Nachrichten quittiert. Der Vorgang steht im Änderungsprotokoll.`,
      );
      invalidate();
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Quittierung fehlgeschlagen."),
  });

  const rows = q.data?.sends ?? [];

  const openAcknowledgements = rows.filter(
    (row) => (FAILED.has(row.status) || onlyPending) && !row.acknowledged_at,
  );

  const nav = (next: Partial<Search>) =>
    void navigate({
      search: {
        status: onlyPending ? "ausstehend" : onlyFailures ? "fehler" : "alle",
        tage: String(days),
        quittung: ack,
        ...next,
      },
    });

  const buttonClass = (active: boolean) =>
    `rounded-md border px-3 py-1.5 font-display text-xs font-extrabold transition-colors ${
      active
        ? "border-brand-petrol bg-brand-petrol text-primary-foreground"
        : "border-border bg-background text-foreground hover:bg-secondary"
    }`;

  return (
    <div className="max-w-6xl">
      <AdminPageHeader
        title="Versandprotokoll"
        description="Reine Einsicht: Was ist versandt worden, und was ist gescheitert? Nachrichten an fremde Empfänger stehen oben."
      />

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-pressed={onlyFailures}
          onClick={() => nav({ status: "fehler" })}
          className={buttonClass(onlyFailures)}
        >
          Nur Fehlschläge
        </button>
        <button
          type="button"
          aria-pressed={onlyPending}
          onClick={() => nav({ status: "ausstehend", quittung: "offen" })}
          className={buttonClass(onlyPending)}
        >
          Ohne Quittung
        </button>
        <button
          type="button"
          aria-pressed={!onlyFailures && !onlyPending}
          onClick={() => nav({ status: "alle" })}
          className={buttonClass(!onlyFailures && !onlyPending)}
        >
          Alle Versände
        </button>
        {[7, 30, 0].map((d) => (
          <button
            key={d}
            type="button"
            aria-pressed={days === d}
            onClick={() => nav({ tage: String(d) })}
            className={buttonClass(days === d)}
          >
            {d === 0 ? "Ohne Zeitgrenze" : `${d} Tage`}
          </button>
        ))}
        {ACK_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            aria-pressed={ack === f.value}
            onClick={() => nav({ quittung: f.value })}
            className={buttonClass(ack === f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {q.isPending ? <AdminSkeletonList /> : null}
      {q.isError ? (
        <AdminErrorNotice
          message={q.error instanceof Error ? q.error.message : "Das Protokoll konnte nicht geladen werden."}
        />
      ) : null}

      {q.data ? (
        <div className="mt-6 space-y-6">
          <AdminCard
            title={
              onlyPending
                ? "Versände ohne Quittung"
                : onlyFailures
                  ? "Gescheiterte Mailversände"
                  : "Mailversände"
            }
            description={
              q.data.days === 0 ? "Zeitraum: ohne Zeitgrenze" : `Zeitraum: letzte ${q.data.days} Tage`
            }
          >
            {openAcknowledgements.length > 1 ? (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-secondary/40 p-3">
                <p className="text-xs text-muted-foreground">
                  {openAcknowledgements.length} offene Vorgänge in dieser Ansicht. Die Quittierung wird
                  mit Person und Zeitpunkt im Änderungsprotokoll vermerkt.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={ackMutation.isPending}
                  onClick={() => ackMutation.mutate(openAcknowledgements.map((r) => r.id))}
                >
                  Alle sichtbaren quittieren
                </Button>
              </div>
            ) : null}

            {rows.length === 0 ? (
              <AdminEmptyState
                title="Keine Einträge"
                description="In dieser Ansicht liegt kein passender Versand vor."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th scope="col" className="py-2 pr-4 font-display text-xs font-extrabold text-brand-petrol">
                        Vorlage
                      </th>
                      <th scope="col" className="py-2 pr-4 font-display text-xs font-extrabold text-brand-petrol">
                        Empfänger
                      </th>
                      <th scope="col" className="py-2 pr-4 font-display text-xs font-extrabold text-brand-petrol">
                        Richtung
                      </th>
                      <th scope="col" className="py-2 pr-4 font-display text-xs font-extrabold text-brand-petrol">
                        Status
                      </th>
                      <th scope="col" className="py-2 pr-4 font-display text-xs font-extrabold text-brand-petrol">
                        Zeitpunkt
                      </th>
                      <th scope="col" className="py-2 pr-4 font-display text-xs font-extrabold text-brand-petrol">
                        Quittierung
                      </th>
                      <th scope="col" className="py-2 font-display text-xs font-extrabold text-brand-petrol">
                        Fehler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rows.map((row) => (
                      <tr key={row.id} className="align-top">
                        <td className="py-3 pr-4 font-display font-extrabold text-brand-petrol">
                          {row.template_name}
                        </td>
                        <td className="py-3 pr-4 text-xs break-all">{row.recipient}</td>
                        <td className="py-3 pr-4">
                          <span
                            className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                              row.is_internal
                                ? "bg-secondary text-muted-foreground"
                                : "bg-brand-orange/15 text-brand-orange"
                            }`}
                          >
                            {row.is_internal ? "intern" : "extern"}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-xs font-semibold text-brand-orange">
                          {onlyPending && row.status === "pending"
                            ? "Versandantwort fehlt"
                            : EMAIL_STATUS_LABELS[row.status] ?? row.status}
                        </td>
                        <td className="py-3 pr-4 text-xs whitespace-nowrap text-muted-foreground">
                          {dateFmt.format(new Date(row.created_at))}
                        </td>
                        <td className="py-3 pr-4 text-xs">
                          {FAILED.has(row.status) || onlyPending ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <FixPromptButton load={() => fixPromptFn({ data: { id: row.id } })} />
                              {row.acknowledged_at ? (
                                <span className="whitespace-nowrap text-muted-foreground">
                                  quittiert am {dateFmt.format(new Date(row.acknowledged_at))}
                                </span>
                              ) : (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={ackMutation.isPending}
                                  onClick={() => ackMutation.mutate([row.id])}
                                >
                                  Quittieren
                                </Button>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-3 text-xs text-muted-foreground">
                          {row.error_message ??
                            (onlyPending && row.status === "pending"
                              ? "Keine Versandantwort protokolliert"
                              : "")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </AdminCard>

          <AdminCard
            title="Gescheiterte Automationsläufe"
            description="Begleitstrecken, die nicht ausgeliefert wurden"
          >
            {q.data.automationRuns.length === 0 ? (
              <AdminEmptyState
                title="Keine Einträge"
                description="In diesem Zeitraum ist kein Automationslauf gescheitert."
              />
            ) : (
              <ul className="divide-y divide-border">
                {q.data.automationRuns.map((row) => (
                  <li key={row.id} className="py-3 text-sm">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <span className="font-display font-extrabold text-brand-petrol">
                        {row.automation_name ?? "Automation"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {dateFmt.format(new Date(row.created_at))}
                      </span>
                    </div>
                    {row.detail ? (
                      <p className="mt-1 text-xs text-muted-foreground">{row.detail}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </AdminCard>
        </div>
      ) : null}
    </div>
  );
}
